import { Chip } from './Chip';
import { StageActivityChips } from './StageActivityChips';
import type { EventStreamStatus } from '@/hooks/useEventStream';
import { formatDuration, formatTime } from '@/utils/format';
import type { BackEdgeInfo, StageSubProgress, StageSummaryDto } from '@/types/api';
import { ROLE_ICON, STAGE_ROLE } from '@/utils/roleTheme';

export function ProgressTracker({
  stages,
  isLive,
  isAwaitingApproval = false,
  streamStatus,
  cursor,
  onReconnect,
  subProgressByStage,
  backEdge,
  attemptByStage
}: {
  stages: ReadonlyArray<StageSummaryDto>;
  isLive: boolean;
  isAwaitingApproval?: boolean;
  streamStatus: EventStreamStatus;
  cursor: string | null;
  onReconnect: () => void;
  subProgressByStage: Readonly<Record<string, StageSubProgress | undefined>>;
  backEdge: BackEdgeInfo | null;
  attemptByStage: Readonly<Record<string, number | undefined>>;
}): JSX.Element {
  return (
    <section
      className="bg-panel border border-border rounded-lg p-4 mb-4"
      data-testid="progress-tracker"
      aria-label="Real-time progress tracker"
    >
      {isLive ? (
        <ConnectionBar status={streamStatus} cursor={cursor} onReconnect={onReconnect} />
      ) : isAwaitingApproval ? (
        <AwaitingApprovalBar />
      ) : null}

      {backEdge ? (
        <div
          className="flex items-center gap-3 px-4 py-3 mb-4 rounded-md border border-border border-l-[3px] border-l-role-architect bg-role-architect/[0.08] text-[13px] text-text-dim"
          data-testid="back-edge-banner"
          role="status"
        >
          <span className="text-role-architect text-lg">↺</span>
          <div>
            <strong className="text-text">Back-edge fired:</strong> {backEdge.from_stage} →{' '}
            {backEdge.to_stage} rework triggered at {formatTime(backEdge.fired_at)} — attempt{' '}
            {backEdge.attempt_number}
            {backEdge.max_attempts !== undefined ? ` of ${backEdge.max_attempts}` : ''} in flight.
          </div>
        </div>
      ) : null}

      <div className="font-semibold text-[13px] mb-3 flex items-center gap-2.5">
        Stage progress
        {isLive ? (
          <span className="text-[11.5px] text-text-muted font-normal ml-auto">
            live · updates within ≤ 2 s of event emission · resumes from cursor on reconnect
          </span>
        ) : isAwaitingApproval ? (
          <span className="text-[11.5px] text-text-muted font-normal ml-auto">
            paused — waiting on an approval decision; updates resume once a decision is recorded
          </span>
        ) : null}
      </div>

      <ol
        className="grid grid-cols-[44px_1fr] gap-y-0"
        data-testid="progress-tracker-list"
      >
        {stages.map((s, i) => {
          const ico = ROLE_ICON[STAGE_ROLE[s.stage_id] ?? s.stage_id] ?? ROLE_ICON['explorer']!;
          const sub = subProgressByStage[s.stage_id];
          const attempt = attemptByStage[s.stage_id];
          const isLast = i === stages.length - 1;
          const stateClass =
            s.state === 'completed'
              ? 'border-ok/40'
              : s.state === 'running'
                ? 'border-accent'
                : s.state === 'failed'
                  ? 'border-err'
                  : 'border-border';
          return (
            <li
              key={s.stage_id}
              className="contents"
              data-testid={`tracker-row-${s.stage_id}`}
            >
              <div className="flex flex-col items-center">
                <span
                  className={`w-[36px] h-[36px] rounded-full grid place-items-center font-mono font-semibold text-[11px] ${
                    s.state === 'pending' || s.state === 'skipped'
                      ? 'bg-panel-3 text-text-muted'
                      : `${ico.bg} ${ico.fg}`
                  } ${s.state === 'running' ? 'animate-pulse' : ''}`}
                >
                  {ico.abbr}
                </span>
                {!isLast ? (
                  <span
                    className={`w-0.5 flex-1 min-h-[12px] ${
                      s.state === 'completed' ? 'bg-ok' : 'bg-border'
                    }`}
                  />
                ) : null}
              </div>
              <div className="pl-3.5 pb-4">
                <div
                  className={`bg-panel-2 border rounded-md px-3.5 py-2.5 ${stateClass}`}
                  data-testid={`tracker-card-${s.stage_id}`}
                >
                  <div className="flex items-center flex-wrap gap-2.5 mb-1.5">
                    <span className="font-medium text-[13px]">{s.stage_id}</span>
                    <Chip state={s.state} />
                    {attempt && attempt > 1 ? (
                      <span
                        className="inline-flex items-center gap-1 font-mono bg-role-architect/[0.12] text-role-architect text-[10px] px-2 py-0.5 rounded-full"
                        data-testid={`rework-badge-${s.stage_id}`}
                      >
                        ↺ rework · attempt {attempt}
                        {backEdge?.max_attempts !== undefined ? ` of ${backEdge.max_attempts}` : ''}
                      </span>
                    ) : null}
                    <StageActivityChips stage={s} />
                    <span className="text-[11.5px] text-text-muted ml-auto tabular-nums">
                      {timeRange(s)}
                    </span>
                  </div>
                  {sub ? (
                    <div className="mt-1.5" data-testid={`tracker-subprogress-${s.stage_id}`}>
                      <div className="text-[11.5px] text-text-dim mb-1">
                        <span className="font-mono text-text-muted">step:</span> {sub.current_step}
                      </div>
                      <div className="h-[6px] bg-panel-3 rounded overflow-hidden">
                        <div
                          className="h-full bg-accent transition-[width] duration-500"
                          style={{ width: `${Math.round(sub.progress_pct)}%` }}
                        />
                      </div>
                      <div className="text-[11.5px] text-text-muted flex gap-4 mt-1.5">
                        <span>
                          <strong className="text-text-dim font-medium">progress</strong>{' '}
                          <span className="font-mono text-text-dim">
                            {Math.round(sub.progress_pct)}%
                          </span>
                        </span>
                        <span>
                          <strong className="text-text-dim font-medium">latency_so_far</strong>{' '}
                          <span className="font-mono text-text-dim">
                            {Math.round(sub.latency_so_far_ms / 1000)} s
                          </span>
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function timeRange(s: StageSummaryDto): string {
  if (s.state === 'completed') {
    return `${formatDuration(s.started_at, s.completed_at)} · ${formatTime(s.started_at)} → ${formatTime(s.completed_at)}`;
  }
  if (s.state === 'running') return `${formatDuration(s.started_at, null)} elapsed`;
  if (s.state === 'failed' && s.completed_at)
    return `failed at ${formatTime(s.completed_at)}`;
  if (s.state === 'paused_for_approval') return 'paused for approval';
  if (s.state === 'skipped') {
    return s.skip_reason && s.skip_reason.trim().length > 0
      ? `skipped — ${s.skip_reason}`
      : 'skipped — nothing to do on this run';
  }
  return '— awaiting upstream —';
}

function AwaitingApprovalBar(): JSX.Element {
  return (
    <div
      className="bg-warn/[0.08] border border-border border-l-[3px] border-l-warn rounded-md px-3.5 py-2.5 mb-3 flex items-center gap-3.5"
      data-testid="awaiting-approval-bar"
      role="status"
    >
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-[12px] text-[11.5px] font-medium bg-warn/15 text-warn">
        <span className="w-1.5 h-1.5 rounded-full bg-warn" />
        Paused · awaiting approval
      </span>
      <span className="text-xs text-text-dim">
        This run is parked on a human decision — it is not running. The timeline
        will resume once the decision is recorded.
      </span>
    </div>
  );
}

function ConnectionBar({
  status,
  cursor,
  onReconnect
}: {
  status: EventStreamStatus;
  cursor: string | null;
  onReconnect: () => void;
}): JSX.Element {
  const pill: Record<EventStreamStatus, { label: string; cls: string; dot: string; pulse: boolean }> = {
    idle: { label: 'idle', cls: 'bg-panel-2 text-text-muted', dot: 'bg-text-muted', pulse: false },
    connecting: {
      label: 'Connecting…',
      cls: 'bg-warn/15 text-warn',
      dot: 'bg-warn',
      pulse: true
    },
    open: {
      label: 'Live · SSE connected',
      cls: 'bg-ok/15 text-ok',
      dot: 'bg-ok',
      pulse: true
    },
    reconnecting: {
      label: 'Reconnecting…',
      cls: 'bg-warn/15 text-warn',
      dot: 'bg-warn',
      pulse: true
    },
    closed: {
      label: 'Disconnected',
      cls: 'bg-err/15 text-err',
      dot: 'bg-err',
      pulse: false
    },
    error: {
      label: 'Disconnected',
      cls: 'bg-err/15 text-err',
      dot: 'bg-err',
      pulse: false
    }
  };
  const p = pill[status];
  return (
    <div
      className="bg-panel-2 border border-border rounded-md px-3.5 py-2.5 mb-3 flex items-center gap-3.5"
      data-testid="connection-bar"
    >
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-[12px] text-[11.5px] font-medium ${p.cls}`}
        data-conn-status={status}
      >
        <span
          className={`w-1.5 h-1.5 rounded-full ${p.dot} ${p.pulse ? 'animate-pulse' : ''}`}
        />
        {p.label}
      </span>
      <span className="text-xs text-text-dim">
        <span className="font-mono text-text">
          last cursor: {cursor ?? '—'}
        </span>
      </span>
      {status === 'reconnecting' ? (
        <span className="text-[11.5px] text-warn ml-auto">
          attempting resume from cursor — no events dropped
        </span>
      ) : status === 'closed' || status === 'error' ? (
        <>
          <span className="ml-auto" />
          <button
            type="button"
            onClick={onReconnect}
            className="bg-err text-white border-0 px-3 py-1 rounded-md text-[11.5px] font-medium cursor-pointer"
            data-testid="reconnect-button"
          >
            Reconnect
          </button>
        </>
      ) : (
        <span className="text-[11.5px] text-text-muted ml-auto">
          latency target ≤ 2 s
        </span>
      )}
    </div>
  );
}
